import React, { useMemo, useState } from "react";
import { MdSearch, MdWarningAmber } from "react-icons/md";
import Card from "../../../../components/card";
import { Profile, UserProfiles } from "../../../../interfaces/Profile";

interface Props {
  users: UserProfiles[];
  profiles: Profile[];
  onAssign: (userId: string, profileIds: number[]) => Promise<void>;
}

/**
 * Quem tem qual perfil. É a outra metade da pergunta — a matriz diz o que um perfil pode, esta
 * tabela diz sobre quem isso incide.
 *
 * Salva a cada clique, sem botão: a alternativa seria um estado "sujo" por linha numa tabela que
 * pode ter dezenas de pessoas, e alguém sairia da tela achando que tinha salvo.
 */
const UserProfilesTable: React.FC<Props> = ({ users, profiles, onAssign }) => {
  const [search, setSearch] = useState("");
  const [savingUserId, setSavingUserId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? users.filter(
          (u) =>
            (u.name ?? "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
        )
      : users;
    return [...list].sort((a, b) =>
      (a.name ?? a.email).localeCompare(b.name ?? b.email, "pt-BR")
    );
  }, [users, search]);

  const toggle = async (user: UserProfiles, profileId: number) => {
    // Cada pessoa tem um perfil só (o servidor recusa dois): marcar troca o perfil, desmarcar deixa sem.
    const next = user.profileIds.includes(profileId) ? [] : [profileId];
    setSavingUserId(user.userId);
    try {
      await onAssign(user.userId, next);
    } finally {
      setSavingUserId(null);
    }
  };

  // Perfil externo se dá no vínculo (Acessos/Pessoas/Painéis); o servidor recusa aqui. Sem `alcance`
  // (backend antigo) não há como saber, então oferece todos. Externo que alguém já tem (atribuído aqui
  // antes da onda 3.1) continua com coluna, só para desmarcar: sem ela não haveria como tirá-lo pela tela.
  const ehExterno = (p: Profile) => p.alcance === "externo";
  const active = profiles.filter(
    (p) => p.flActive && (!ehExterno(p) || users.some((u) => u.profileIds.includes(p.id)))
  );
  const semPerfil = filtered.filter((u) => u.profileIds.length === 0).length;

  return (
    <Card extra="w-full px-6 py-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou e-mail"
            className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          />
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {filtered.length} {filtered.length === 1 ? "usuário" : "usuários"}
        </span>
      </div>

      {semPerfil > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <MdWarningAmber className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {semPerfil} {semPerfil === 1 ? "pessoa está" : "pessoas estão"} sem perfil. O acesso
            delas ainda é deduzido do papel antigo — atribua um perfil para que esta tela passe a
            valer para elas também.
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr className="border-b border-gray-100 dark:border-white/10">
              <th className="py-2.5 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Pessoa
              </th>
              {active.map((p) => (
                <th
                  key={p.id}
                  className="px-3 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                >
                  {p.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr
                key={u.userId}
                className={`border-b border-gray-50 transition-opacity dark:border-white/5 ${
                  savingUserId === u.userId ? "opacity-50" : ""
                }`}
              >
                <td className="py-2.5 pr-4">
                  <span className="block text-sm font-medium text-navy-700 dark:text-white">
                    {u.name ?? "—"}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">{u.email}</span>
                </td>
                {active.map((p) => (
                  <td key={p.id} className="px-3 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={u.profileIds.includes(p.id)}
                      disabled={
                        savingUserId === u.userId || (ehExterno(p) && !u.profileIds.includes(p.id))
                      }
                      onChange={() => toggle(u, p.id)}
                      aria-label={`${p.name} para ${u.name ?? u.email}`}
                      className="h-4 w-4 accent-brand-500"
                    />
                  </td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={active.length + 1}
                  className="py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  Nenhum usuário encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default UserProfilesTable;
